import { useState } from "react";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFull from "@mui/icons-material/OpenInFull";
import ExUnits from "./ExUnits";
import ExTraits from "./ExTraits";
import ExItems from "./ExItems";
import ExAugments from "./ExAugments";
import UnitImage from "common/src/tft-static/UnitImage";
import * as TraitImage from "common/src/tft-static/TraitImage";
import ItemImage from "common/src/tft-static/ItemImage";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { make as UnitImageExtended } from "common/src/tft-static/UnitImageExtended.bs";
import type { ExplData, Filter, Action } from "./ExState";
import { Tabs, Tab, Select, MenuItem } from "@mui/material";
import { allUnitFilters, allTraitFilters } from "./ExState";
import { common as t } from "common/src/bindings/Translation2";

type tab = "Units" | "Traits" | "Items" | "Augments";

const renderFilter = (f: Filter) => {
  switch (f.typ) {
    case "u": {
      if (f.starLevel === 0) {
        return <UnitImage unitId={f.id} size={32} />;
      } else {
        return (
          <UnitImageExtended
            unitId={f.id}
            size={32}
            starLevel={f.starLevel}
            items={[]}
            forceStarLevel
          />
        );
      }
    }
    case "t":
      return <TraitImage.PlainIcon2 traitId={f.id} rank={f.rank} size={32} />;
    case "i":
      return <ItemImage itemId={String(f.id)} size={30} rounded="rounded" />;
    case "a":
      return <AugmentImage augId={String(f.id)} size={32} />;
  }
};

// Nunu Kog
// Syndra Cassio
// Fiora Gwen
// Akali Nilah
// Chrono Karma
// Mage Veigar
const ex1Filters: Array<Filter> = [
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Nunu")!,
    starLevel: 3,
  },
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_KogMaw")!,
    starLevel: 3,
  },
];
const ex2Filters: Array<Filter> = [
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Syndra")!,
    starLevel: 3,
  },
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Cassiopeia")!,
    starLevel: 3,
  },
];
const ex3Filters: Array<Filter> = [
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Fiora")!,
  },
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Gwen")!,
  },
];
const ex4Filters: Array<Filter> = [
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Akali")!,
    starLevel: 3,
  },
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Nilah")!,
    starLevel: 3,
  },
];
const ex5Filters: Array<Filter> = [
  {
    ...allUnitFilters().find((f) => f.id === "TFT12_Veigar")!,
    starLevel: 3,
  },
  allTraitFilters().find((f) => f.id === "TFT12_Mage" && f.rank === 2)!,
];
const ex6Filters: Array<Filter> = [
  allUnitFilters().find((f) => f.id === "TFT12_Karma")!,
  allTraitFilters().find((f) => f.id === "TFT12_Chrono" && f.rank === 2)!,
];

const getTable = (
  tab: tab,
  data: ExplData,
  onExpand: (f: Filter) => void,
  hideExpand: boolean,
  minSample: number,
  hidePlacements: boolean,
) => {
  switch (tab) {
    case "Units":
      return (
        <ExUnits
          data={data.units}
          starData={data.star_units}
          baseCount={data.base.count}
          onExpand={onExpand}
          hideExpand={hideExpand}
          minSample={minSample}
          hidePlacements={hidePlacements}
        />
      );
    case "Traits":
      return (
        <ExTraits
          data={data.traits}
          baseCount={data.base.count}
          onExpand={onExpand}
          hideExpand={hideExpand}
          minSample={minSample}
          hidePlacements={hidePlacements}
        />
      );
    case "Items":
      return (
        <ExItems
          data={data.items}
          baseCount={data.base.count}
          onExpand={onExpand}
          hideExpand={hideExpand}
          minSample={minSample}
          hidePlacements={hidePlacements}
        />
      );
    case "Augments":
      return (
        <ExAugments
          data={data.augments}
          baseCount={data.base.count}
          onExpand={onExpand}
          hideExpand={hideExpand}
          minSample={minSample}
        />
      );
  }
};

type DataTableProps = {
  queryData: undefined | [Array<Filter>, ExplData | undefined];
  onExpand: (f: Filter) => void;
  dispatch: (a: Action) => void;
  hidePlacements: boolean;
};

const DataTable = ({
  queryData,
  onExpand,
  dispatch,
  hidePlacements,
}: DataTableProps) => {
  const [tab, setTab] = useState<tab>("Units");
  const [minSample, setMinSample] = useState(100);

  if (queryData == null) {
    return (
      <div className="text-xl font-montserrat font-semibold text-white2 p-4">
        No Data
      </div>
    );
  } else {
    const [filters, data] = queryData;
    const imgSize = 36;
    const rowClass =
      "flex items-center gap-[6px] p-[10px] rounded cursor-pointer bg-bg2";
    if (filters.length === 0) {
      return (
        <div className="p-4 flex flex-col items-start gap-2 text-base">
          <div className="text-xl">{t("Explore Stats By Adding Filters")}</div>
          <div>
            {t(
              "You can compare units, traits, augments, and items by adding and customizing filters.",
            )}
          </div>
          {/* <div>
            {t(
              "You can try the Explorer by clicking on the preset examples below.",
            )}
          </div>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 pt-4">
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex1Filters])}
              >
                <UnitImageExtended
                  unitId={"TFT12_Nunu"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <AddIcon />
                <UnitImageExtended
                  unitId={"TFT12_KogMaw"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex2Filters])}
              >
                <UnitImageExtended
                  unitId={"TFT12_Syndra"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <AddIcon />
                <UnitImageExtended
                  unitId={"TFT12_Cassiopeia"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex3Filters])}
              >
                <UnitImage unitId={"TFT12_Fiora"} size={imgSize} />
                <AddIcon />
                <UnitImage unitId={"TFT12_Gwen"} size={imgSize} />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex4Filters])}
              >
                <UnitImageExtended
                  unitId={"TFT12_Akali"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <AddIcon />
                <UnitImageExtended
                  unitId={"TFT12_Nilah"}
                  starLevel={3}
                  items={[]}
                  size={imgSize}
                />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex5Filters])}
              >
                <UnitImageExtended
                  unitId={"TFT12_Veigar"}
                  size={imgSize}
                  starLevel={3}
                  items={[]}
                />
                <AddIcon />
                <TraitImage.PlainIcon2
                  traitId={"TFT12_Mage"}
                  rank={2}
                  size={imgSize}
                />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div
                className={rowClass}
                onClick={(_) => dispatch(["set_all_filters", ex6Filters])}
              >
                <UnitImage unitId={"TFT12_Karma"} size={imgSize} />
                <AddIcon />
                <TraitImage.PlainIcon2
                  traitId={"TFT12_Chrono"}
                  rank={2}
                  size={imgSize}
                />
                <div className="pl-[6px]">
                  <OpenInFull />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div />
          </div> */}
        </div>
      );
    }
    return (
      <div>
        <div className="p-3 flex items-center gap-4 text-sm sm:text-base">
          <div className="flex items-center gap-2">
            {filters.map((f, idx) => {
              const el = renderFilter(f);
              return (
                <div className="relative">
                  {el}
                  <div
                    className="absolute left-0 top-0 right-0 bottom-0 rounded opacity-0 hover:opacity-100 bg-[#44444488] cursor-pointer z-10 flex items-center justify-center transition"
                    onClick={(_) => dispatch(["set_filter", [idx, undefined]])}
                  >
                    <CloseIcon fontSize="small" />
                  </div>
                </div>
              );
            })}
            {filters.length === 0 ? <div>Diamond+</div> : null}
          </div>
          {data && (
            <>
              <div className="text-white2">
                {t("Games")}:{" "}
                <span className="font-medium text-white1">
                  {humanReadableCount(data.base.count)}
                </span>
              </div>
              {hidePlacements ? null : (
                <>
                  <div className="text-white2">
                    {t("Avg.")}:{" "}
                    <span className="font-medium text-white1">
                      {(data.base.place / data.base.count).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-white2">
                    {t("Top 4")}:{" "}
                    <span className="font-medium text-white1">
                      {formatPct2(data.base.top4 / data.base.count)}
                    </span>
                  </div>
                  <div className="text-white2">
                    {t("Win")}:{" "}
                    <span className="font-medium text-white1">
                      {formatPct2(data.base.won / data.base.count)}
                    </span>
                  </div>
                </>
              )}
            </>
          )}
          <div className="flex items-center ml-auto">
            <div className="text-sm pr-[6px]">{t("Minimum sample size")}</div>
            <Select
              value={minSample}
              label="Minimum sample size"
              onChange={(e) => setMinSample(Number(e.target.value))}
              variant="standard"
              sx={{ width: 46 }}
              classes={{
                select: "!pr-[18px] py-[3px] text-[15px]",
                icon: "my-[2px] w-[20px] h-[20px]",
              }}
            >
              <MenuItem value={10000}>10k</MenuItem>
              <MenuItem value={3000}>3k</MenuItem>
              <MenuItem value={1000}>1k</MenuItem>
              <MenuItem value={300}>300</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </div>
        </div>
        {data ? (
          <>
            <div className="p-2">
              <Tabs value={tab} onChange={(_: any, t: tab) => setTab(t)}>
                <Tab label={t("Units")} value="Units" />
                <Tab label={t("Traits")} value="Traits" />
                <Tab label={t("Items")} value="Items" />
                {/* <Tab label={t("Augments")} value="Augments" /> */}
              </Tabs>
            </div>
            {getTable(
              tab,
              data,
              onExpand,
              filters.length >= 3,
              minSample,
              hidePlacements,
            )}
          </>
        ) : (
          <div className="text-xl font-montserrat font-semibold text-white2 p-4">
            No Data
          </div>
        )}
      </div>
    );
  }
};

export default DataTable;
